import { useEffect, useRef } from 'react';
import type { GroupChatMessage } from '../../../types/groups';
import GroupMessageItem from './GroupMessageItem';

interface GroupMessageListProps {
  readonly messages: GroupChatMessage[];
  readonly scrollToMessageId?: number;
}

export default function GroupMessageList({ messages, scrollToMessageId }: GroupMessageListProps) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const bottomRef = useRef<HTMLDivElement | null>(null);
  const hasScrolledToTargetRef = useRef(false);

  // Reset scroll-to-target flag when the target message changes
  useEffect(() => {
    hasScrolledToTargetRef.current = false;
  }, [scrollToMessageId]);

  useEffect(() => {
    if (!messages.length) return;

    if (scrollToMessageId && !hasScrolledToTargetRef.current) {
      const target = containerRef.current?.querySelector(
        `[data-message-id="${scrollToMessageId}"]`
      ) as HTMLElement | null;

      if (target) {
        hasScrolledToTargetRef.current = true;
        target.scrollIntoView({ behavior: 'smooth', block: 'center' });
        // Briefly highlight the target message
        target.style.transition = 'background-color 0.4s ease';
        target.style.backgroundColor = '#fef9c3';
        setTimeout(() => {
          target.style.backgroundColor = '';
        }, 2500);
      }
    } else if (!scrollToMessageId) {
      bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages, scrollToMessageId]);

  return (
    <div ref={containerRef} className="flex-1 overflow-y-auto bg-gray-50 px-4 py-5 sm:px-6">
      <div className="mx-auto flex max-w-4xl flex-col gap-5">
        {messages.map((message) => (
          <div key={message.messageId} data-message-id={message.messageId}>
            <GroupMessageItem message={message} />
          </div>
        ))}
        <div ref={bottomRef} />
      </div>
    </div>
  );
}
